import { defineStore } from 'pinia'

export type Tool = 'orbit' | 'paint' | 'erase' | 'select' | 'eyedropper'
export type VoxelDocument = { grid_size: number; colors: Record<string, string>; data: string[][] }

const symbols = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'

function makeSample(): VoxelDocument {
  const size = 24
  const cells = Array.from({ length: size }, () => Array.from({ length: size }, () => '.'.repeat(size)))
  const write = (x: number, y: number, z: number, symbol: string) => {
    const row = cells[z][y]
    cells[z][y] = `${row.slice(0, x)}${symbol}${row.slice(x + 1)}`
  }
  for (let z = 5; z < 18; z += 1) for (let y = 6; y < 18; y += 1) for (let x = 6; x < 18; x += 1) {
    if (x === 6 || x === 17 || y === 6 || y === 17 || z === 5 || z === 17) write(x, y, z, z > 14 ? 'B' : 'A')
  }
  for (let z = 8; z < 13; z += 1) for (let y = 9; y < 15; y += 1) for (let x = 9; x < 15; x += 1) write(x, y, z, 'C')
  return { grid_size: size, colors: { A: '#3777d6', B: '#8ab5ff', C: '#f4c364' }, data: cells }
}

export const useEditorStore = defineStore('editor', {
  state: () => ({
    document: makeSample() as VoxelDocument,
    revision: 0,
    selectedSymbol: 'A',
    tool: 'orbit' as Tool,
    selected: new Set<string>(),
    projectName: 'Untitled sculpture',
    isFocused: false,
    connected: false,
    collaborators: [{ id: 'local', name: 'You', color: '#3d7df5' }],
    undoStack: [] as VoxelDocument[],
    redoStack: [] as VoxelDocument[],
    status: 'Ready',
  }),
  getters: {
    occupied(state): number {
      return state.document.data.reduce((total, slice) => total + slice.reduce((sum, row) => sum + [...row].filter((cell) => cell !== '.').length, 0), 0)
    },
  },
  actions: {
    snapshot(): VoxelDocument { return structuredClone(this.document) },
    commit(next: VoxelDocument, label: string) {
      this.undoStack.push(this.snapshot())
      if (this.undoStack.length > 100) this.undoStack.shift()
      this.redoStack = []
      this.document = next
      this.revision += 1
      this.status = label
    },
    setVoxel(x: number, y: number, z: number, symbol: string, label = 'Edited voxel') {
      const size = this.document.grid_size
      if (![x, y, z].every((coordinate) => coordinate >= 0 && coordinate < size)) return
      const next = this.snapshot()
      const row = next.data[z][y]
      if (row[x] === symbol) return
      next.data[z][y] = `${row.slice(0, x)}${symbol}${row.slice(x + 1)}`
      this.commit(next, label)
    },
    select(key: string, additive = false) {
      if (!additive) this.selected.clear()
      this.selected.add(key)
      this.revision += 1
      this.status = `${this.selected.size} selected`
    },
    undo() {
      const previous = this.undoStack.pop()
      if (!previous) return
      this.redoStack.push(this.snapshot()); this.document = previous; this.revision += 1; this.status = 'Undo'
    },
    redo() {
      const next = this.redoStack.pop()
      if (!next) return
      this.undoStack.push(this.snapshot()); this.document = next; this.revision += 1; this.status = 'Redo'
    },
    addPalette(hex: string) {
      const symbol = symbols.split('').find((item) => !this.document.colors[item])
      if (!symbol) return
      this.document.colors[symbol] = hex.toUpperCase(); this.selectedSymbol = symbol; this.revision += 1
    },
    load(document: VoxelDocument, name: string) {
      this.document = document; this.projectName = name; this.selected.clear(); this.undoStack = []; this.redoStack = []; this.revision += 1; this.status = 'Imported document'
    },
  },
})

